import {
  finleyAssert,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { typeguards } from '@finley-technologies/finley-types';

import { FundingRequestStatusEnumGQLType } from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import type {
  LoanActivity,
  FundingRequestsFormState,
  EditLoanFormFields,
} from './types';
import {
  EditLoanContextType,
  EditLoanContextReady,
} from './useEditLoanContext';

const _fundingRequestStatusToActivityStatus = ({
  status,
}: {
  status: FundingRequestStatusEnumGQLType;
}) => {
  switch (status) {
    case 'approved':
      return 'approved';
    case 'rejected':
      return 'rejected';
    case 'submitted':
      return 'submitted';
    case 'unsubmitted':
    case 'pending':
      return 'pending';
    case 'expired':
    case 'canceled':
      return 'canceled';
    case 'canceledLoanEdit':
    case 'pendingLoanEdit':
    case 'overriddenFromLoanEdit':
      return 'unknown'; // need to clarify what the loan edit statuses are
    default:
      return finleyAssertUnreachable(status);
  }
};

const getFundingRequestLoanActivity = ({
  context,
  currentFundingRequests,
}: {
  context: EditLoanContextReady;
  currentFundingRequests: FundingRequestsFormState;
}): LoanActivity[] => {
  const { originalResolvedLoan, newEntities } = context.state;
  const facility = originalResolvedLoan.facility;

  // If "newEntities" is available, then we're reviewing a loan edit and can
  // utilize the newResolvedLoan and changeset metadata to help generate the
  // "loan activity" that we'll display to the user.
  // Note that some of the proposed changes only resides in the "changeset metadata"
  // and now on the new resolved loan yet so need to incorporate that in when
  // checking against anything that's changed
  if (newEntities) {
    finleyAssert(
      typeguards.loan.isEditSubmittedLoanActionMetadata(
        newEntities.loanAction.metadata,
      ),
      `Detected invalid loan action edit metadata structure while generating form fields`,
    );

    const { fundingRequestChangesets } = newEntities.loanAction.metadata;

    return newEntities.newResolvedLoan.funding.requests.map((fr) => {
      const fundingChangeSet = fundingRequestChangesets.find(
        (x) => x.newFundingRequestId === fr.fundingRequestId,
      );
      finleyAssert(
        fundingChangeSet,
        `Failed to find funding request changeset while organizing loan activity [fundingRequestId:${fr.fundingRequestId}]`,
      );

      const originalFundingRequest = originalResolvedLoan.funding.requests.find(
        (x) => x.fundingRequestId === fundingChangeSet.originalFundingRequestId,
      );
      finleyAssert(
        originalFundingRequest,
        `Failed to find original funding request while organizing loan activity [fundingRequestId:${fr.fundingRequestId}]`,
      );

      const didRequestChange =
        fr.totalRequestedAmount !== originalFundingRequest.totalRequestedAmount;

      const didComponentsChange = fr.fundingComponents.some((frc) => {
        const componentChangeset = fundingChangeSet.fundingRequestComponents.find(
          (x) =>
            x.newFundingRequestComponentId === frc.fundingRequestComponentId,
        );
        finleyAssert(
          componentChangeset,
          `Failed to find funding component changeset while generating form fields [fundingRequestComponentId:${frc.fundingRequestComponentId}]`,
        );

        const originalComponent = originalFundingRequest.fundingComponents.find(
          (x) =>
            x.fundingRequestComponentId ===
            componentChangeset.originalFundingRequestComponentId,
        );
        finleyAssert(
          originalComponent,
          `Failed to find original funding component while generating form fields [fundingRequestComponentId:${frc.fundingRequestComponentId}]`,
        );

        return (
          frc.requestedAmount !== originalComponent.requestedAmount ||
          frc.targetFundingDate !== originalComponent.targetFundingDate ||
          componentChangeset.postingDate !== originalComponent.postingDate ||
          componentChangeset.fundTransferDate !==
            originalComponent.fundTransferDate
        );
      });

      return {
        activityId: fr.fundingRequestId,
        amount: fr.totalRequestedAmount,
        effectiveDate: fr.originationDate,
        facility,
        type: 'Funding Request',
        isEdited: didRequestChange || didComponentsChange,
        status: _fundingRequestStatusToActivityStatus({
          status: fr.fundingRequestStatus,
        }),
      };
    });
  }

  // If "newEntities" is not available (per above), then we're editing the
  // current loan directly. We can just set the original loan as the activity
  // data and check against the current form values
  return originalResolvedLoan.funding.requests.map((fr) => {
    const currentFundingRequest = currentFundingRequests[fr.fundingRequestId];
    const { totalAmount, components } = currentFundingRequest;

    const isEdited =
      fr.totalRequestedAmount !== totalAmount ||
      fr.fundingComponents.some((frc) => {
        const newComponent = components[frc.fundingRequestComponentId];
        return (
          frc.requestedAmount !== newComponent.amount ||
          frc.targetFundingDate !== currentFundingRequest.targetFundingDate
        );
      });

    return {
      activityId: fr.fundingRequestId,
      amount: totalAmount,
      // @jendevelops TODO: need to pipe in the actual date of the funding request (probably not origination but request date)
      effectiveDate: fr.originationDate,
      facility,
      type: 'Funding Request',
      isEdited,
      status: _fundingRequestStatusToActivityStatus({
        status: fr.fundingRequestStatus,
      }),
    };
  });
};

export const getLoanActivity = ({
  context,
  currentFundingRequests,
}: {
  context: EditLoanContextType;
  currentFundingRequests: EditLoanFormFields['fundingRequests'];
}): LoanActivity[] => {
  if (context.status !== 'ready') {
    return [];
  }

  const loanActivities: LoanActivity[] = [];

  const fundingActivity = getFundingRequestLoanActivity({
    context,
    currentFundingRequests,
  });
  loanActivities.push(...fundingActivity);

  return loanActivities.sort((a, b) =>
    a.effectiveDate.localeCompare(b.effectiveDate),
  );
};
